import React, { useEffect } from "react";
import Form from "../../Form";
import styles from "./CrmFilterDrawer.module.scss";
import { useFormContext } from "react-hook-form";
import moment from "moment";
import { ButtonShape, ButtonSize } from "../../../enums/ButtonAttributesEnums";
import TNButton from "../../../reusable-components/TNButton";
import { InputfieldTypes } from "../../../enums/InputFieldAttributesEnums";
import { disableFutureDates } from "../../../utils/disabledDateUtil";
import { fieldNames, filterFields, jobStatusFilterOptions } from "./constants";
import { CrmFilterConstants } from "./constants";
import { PlacementPositions } from "../../../enums/PlacementPositionsEnums";
import { DrawerHeaderStyle } from "../../../constants/CustomStyles";
import CreateJobServices from "../../../services/createJob.services";
import {
  getDropdownOptions,
  getSalesPersonDropdownOptions,
} from "../../../utils/getDropdownOptions";
import { Drawer } from "antd";
import InputField from "../../../reusable-components/TNInputField";

export interface CrmFilterDrawerProps {
  isOpen: boolean;
  handleClose: () => void;
  filterValue: {};
  setFilterValue: React.Dispatch<React.SetStateAction<{}>>;
  handleClearFilter: () => void;
  handleApply: (jobStatus, customerAccount, salesPerson, dateRange) => void;
  clearAllFilterButton: boolean;
  handleDisableClearAllFilterButton: () => void;
}

const CrmFilterDrawer = ({
  isOpen,
  handleClose,
  filterValue,
  setFilterValue,
  handleClearFilter,
  handleApply,
  clearAllFilterButton,
  handleDisableClearAllFilterButton,
}: CrmFilterDrawerProps) => {
  const { watch, getValues, setValue } = useFormContext();

  const { FetchSalesPerson, FetchCustomerAccounts } = CreateJobServices();

  const { data: salesPersonData, isFetching: isSalesPersonLoading } =
    FetchSalesPerson();
  const { data: customerAccountsData, isPending: isCustomerAccountsLoading } =
    FetchCustomerAccounts();
  const salesOptions = getSalesPersonDropdownOptions(salesPersonData);
  const customerAccountOptions = getDropdownOptions(customerAccountsData);

  const filterPersistanceHandler = () => {
    if (
      !!filterValue?.customerName &&
      !!!getValues(fieldNames.CUSTOMER_FILTER_NAME)
    )
      setValue(fieldNames.CUSTOMER_FILTER_NAME, filterValue?.customerName);
    if (
      !!filterValue?.jobStatus &&
      !!!getValues(fieldNames.JOBSTATUS_FILTER_NAME)
    )
      setValue(fieldNames.JOBSTATUS_FILTER_NAME, filterValue?.jobStatus);
    if (
      !!filterValue?.salesPerson &&
      !!!getValues(fieldNames.SALES_PERSON_FILTER_NAME)
    )
      setValue(fieldNames.SALES_PERSON_FILTER_NAME, filterValue?.salesPerson);

    if (
      !!!filterValue?.customerName &&
      !!getValues(fieldNames.CUSTOMER_FILTER_NAME)
    )
      setValue(fieldNames.CUSTOMER_FILTER_NAME, undefined);
    if (
      !!!filterValue?.jobStatus &&
      !!getValues(fieldNames.JOBSTATUS_FILTER_NAME)
    )
      setValue(fieldNames.JOBSTATUS_FILTER_NAME, undefined);
    if (
      !!!filterValue?.salesPerson &&
      !!getValues(fieldNames.SALES_PERSON_FILTER_NAME)
    )
      setValue(fieldNames.SALES_PERSON_FILTER_NAME, undefined);

    if (
      !!filterValue?.jobStatus &&
      !!getValues(fieldNames.JOBSTATUS_FILTER_NAME) &&
      filterValue?.jobStatus !== getValues(fieldNames?.JOBSTATUS_FILTER_NAME)
    )
      setValue(fieldNames.JOBSTATUS_FILTER_NAME, filterValue?.jobStatus);
    if (
      !!filterValue?.customerName &&
      !!getValues(fieldNames.CUSTOMER_FILTER_NAME) &&
      filterValue?.customerName !== getValues(fieldNames?.CUSTOMER_FILTER_NAME)
    )
      setValue(fieldNames.CUSTOMER_FILTER_NAME, filterValue?.customerName);
    if (
      !!filterValue?.salesPerson &&
      !!getValues(fieldNames.SALES_PERSON_FILTER_NAME) &&
      filterValue?.salesPerson !==
        getValues(fieldNames?.SALES_PERSON_FILTER_NAME)
    )
      setValue(fieldNames.SALES_PERSON_FILTER_NAME, filterValue?.salesPerson);

    if (
      !!filterValue?.fromDate &&
      !!!getValues(fieldNames?.DATE_RANGE_FILTER_NAME)
    )
      setValue(fieldNames.DATE_RANGE_FILTER_NAME, [
        moment(filterValue?.fromDate),
        moment(filterValue?.toDate),
      ]);
    if (
      !!!filterValue?.fromDate &&
      !!getValues(fieldNames?.DATE_RANGE_FILTER_NAME)
    )
      setValue(fieldNames.DATE_RANGE_FILTER_NAME, [undefined, undefined]);
    if (
      !!filterValue?.fromDate &&
      !!getValues(fieldNames?.DATE_RANGE_FILTER_NAME) &&
      filterValue?.fromDate !==
        getValues(fieldNames.DATE_RANGE_FILTER_NAME)[0].valueOf() &&
      filterValue?.toDate !==
        getValues(fieldNames.DATE_RANGE_FILTER_NAME)[1].valueOf()
    )
      setValue(fieldNames.DATE_RANGE_FILTER_NAME, [
        moment(filterValue?.fromDate),
        moment(filterValue?.toDate),
      ]);
  };

  const getFilterOptions = (fieldName: string) => {
    switch (fieldName) {
      case fieldNames.JOBSTATUS_FILTER_NAME:
        return jobStatusFilterOptions;
      case fieldNames.CUSTOMER_FILTER_NAME:
        return customerAccountOptions;
      case fieldNames.SALES_PERSON_FILTER_NAME:
        return salesOptions;
      default:
        return [];
    }
  };

  const handleCloseDrawer = () => {
    filterPersistanceHandler();
    handleClose();
  };

  useEffect(() => {
    if (!clearAllFilterButton) return;
    setValue(fieldNames.JOBSTATUS_FILTER_NAME, undefined);
    setValue(fieldNames.CUSTOMER_FILTER_NAME, undefined);
    setValue(fieldNames.SALES_PERSON_FILTER_NAME, undefined);
    setValue(fieldNames.DATE_RANGE_FILTER_NAME, [undefined, undefined]);
    handleDisableClearAllFilterButton();
  }, [clearAllFilterButton]);

  return (
    <Drawer
      visible={isOpen}
      maskClosable={false}
      destroyOnClose
      closable={true}
      mask={true}
      getContainer={false}
      onClose={handleCloseDrawer}
      placement={PlacementPositions.RIGHT}
      width={CrmFilterConstants.DRAWER_WIDTH}
      height={CrmFilterConstants.DRAWER_HEIGHT}
      title={
        <div className={styles.DrawerHeaderContainer}>
          <p>{CrmFilterConstants.FILTERS_TITLE}</p>
        </div>
      }
      headerStyle={DrawerHeaderStyle}
      size={ButtonSize.LARGE}
      footer={
        <TNButton
          className={styles.submitFilterButton}
          shape={ButtonShape.DEFAULT}
          size={ButtonSize.LARGE}
          text={CrmFilterConstants.SHOW_RESULT_LABEL}
          onClick={() => {
            handleApply(
              getValues(fieldNames.JOBSTATUS_FILTER_NAME),
              getValues(fieldNames.CUSTOMER_FILTER_NAME),
              getValues(fieldNames.SALES_PERSON_FILTER_NAME),
              getValues(fieldNames.DATE_RANGE_FILTER_NAME),
            );
          }}
        />
      }
    >
      <div className={styles.clearWrapper}>
        <span
          className={styles.clearTextContainer}
          onClick={() => {
            handleClearFilter();
            handleClose();
          }}
        >
          {CrmFilterConstants.CLEAR_FILTER_LABEL}
        </span>
      </div>
      <div className={styles.filterFieldsWrapper}>
        {filterFields?.map((field) => {
          const handleChange = (newValue) => {
            setValue(field.name, newValue);
          };

          return (
            <InputField
              type={InputfieldTypes.MULTIPLE_SELECT}
              name={field.name}
              label={field.label}
              placeholder={CrmFilterConstants.SELECT_PLACEHOLDER}
              options={getFilterOptions(field.name)}
              values={watch(field.name)}
              onChange={handleChange}
              resetCheckbox={() => handleChange([])}
              bordered
            />
          );
        })}
        <InputField
          type={InputfieldTypes.RANGE_PICKER}
          name={fieldNames.DATE_RANGE_FILTER_NAME}
          label={CrmFilterConstants.DATE_PICKER_LABEL}
          disabledDates={disableFutureDates}
          allowDateClear
        />
      </div>
    </Drawer>
  );
};

export default CrmFilterDrawer;
